import { useAppDispatch } from '@/hooks';
import { setToken } from '@/store';
import { useVerifyNumberMutation, useVerifyTelegramMutation } from '@/store/api/mfaApi';
import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

type OTPModalProps = {
  open: boolean;
  onClose: () => void;
  data: { username: string; type: string };
};

const OTPModal = ({ open, onClose, data }: OTPModalProps) => {
  const [verifyNumber, { isLoading: numberLoading }] = useVerifyNumberMutation();
  const [verifyTelegram, { isLoading: telegramLoading }] = useVerifyTelegramMutation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [code, setCode] = useState(Array(6).fill(''));
  const inputs = useRef<HTMLInputElement[]>([]);

  if (!open) return null;

  const handleChange = (value: string, index: number) => {
    if (!/^[0-9]?$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    if (value && index < 5) {
      inputs.current[index + 1]?.focus();
    }
  };

  const handleBackspace = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputs.current[index - 1]?.focus();
    }
  };

  const handleSubmit = async () => {
    const otp = code.join('');

    if (otp.length !== 6) {
      toast.error('Please enter complete OTP');
      return;
    }

    try {
      let result;

      if (data.type === 'telegram') {
        result = await verifyTelegram({
          username: data.username,
          token: otp,
        }).unwrap();
        dispatch(setToken(result.accessToken));
        navigate('/home');
      } else {
        result = await verifyNumber({
          username: data.username,
          token: otp,
        }).unwrap();
        dispatch(setToken(result.accessToken));
      }
      navigate('/home');
      localStorage.setItem('showWelcomeBanner', 'true');

      console.log('Verification Success:', result);
      onClose();
    } catch (error: any) {
      console.log('Verification Failed:', error);
      toast.error(error?.data?.message || 'Verification failed');
    }
  };

  const loading = numberLoading || telegramLoading;

  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-50">
      <div className="bg-white rounded-md shadow-lg w-[420px] p-6 text-center">
        <h2 className="text-[#3b6c8c] font-semibold text-lg">Security Code Verification</h2>
        <div className="flex justify-center gap-3 mt-6">
          {code.map((digit, index) => (
            <input
              key={index}
              ref={(el) => {
                if (el) inputs.current[index] = el;
              }}
              type="text"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(e.target.value, index)}
              onKeyDown={(e) => handleBackspace(e, index)}
              className="w-12 h-12 border border-gray-300 rounded text-center text-lg focus:outline-none focus:border-blue-500"
            />
          ))}
        </div>

        <div className="mt-6 flex justify-center gap-3">
          <button
            onClick={() => {
              setCode(Array(6).fill(''));
              onClose();
            }}
            className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
          >
            Cancel
          </button>

          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-4 py-2 hover:bg-green-800 hover:text-white bg-primary disabled:opacity-50"
          >
            {loading ? 'Verifying...' : 'Verify'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default OTPModal;
